/**
 * 모든 게임 밸런스 상수의 단일 출처 (지시서 §7). 밸런스 패치 = 이 파일 수정.
 * 초기값은 검증된 프로토타입 v0.4에서 추출. 본편은 여기서 조정한다.
 */
import type { BuildId, BuildKind, Grade, ParcelType } from './types.js';

/** 월드 격자 (프로토타입 10×14). 본편은 설정값으로 확장. */
export const WORLD = {
  GW: 10, // 가로 타일 수
  GD: 14, // 남북 타일 수
  TILE: 22, // 타일 한 변 (월드 유닛)
  IN: 5, // 필지 내부 격자 (5×5)
  FRONT_ROW_INIT: 4, // 이 행(북쪽 index) 미만 = 마왕령
  FOREST_CHANCE: 0.24, // 타일이 숲일 확률
} as const;

/** 전선 전진/후퇴. */
export const FRONT = {
  /** 잡몹 누적 처치 임계 (1칸 전진). 클수록 개척이 오래 걸린다 */
  PUSH_KILLS_PER_ROW: 30,
  /** 방어 실패가 이만큼 누적되면 전선 1칸 후퇴 */
  RETREAT_FAILS: 3,
  /** 후퇴 한계 (이 row를 넘어 남하하지 않음) */
  MAX_ROW: 7,
} as const;

/**
 * 화폐 싱크 (지시서 3.5 — 인플레 방지가 유저 경제 게임의 생존 조건).
 * 시즌 내내 금이 계속 빠져나가야 매출이 의미를 갖는다.
 */
export const SINK = {
  /** 보유세 주기(초) */
  TAX_INTERVAL: 30,
  /** 필지 1개당 보유세 */
  PARCEL_TAX: 4,
  /** 시설 1개당 유지비 (보유세와 함께 징수) */
  UPKEEP_PER_BUILD: 1,
  /** 판매 시 시장 수수료 비율 (매출에서 이만큼 증발) */
  MARKET_FEE_RATE: 0.08,
} as const;

/** 마왕령 압축 보정 — 영토가 좁아질수록 양은 줄고 격은 오른다 (지시서 3.4). */
export const SPAWN = {
  /** 모험가 일행 유입 주기(초) — 클수록 모험가가 귀해진다 */
  GROUP_INTERVAL: 14,
  /** 동시 존재 가능한 일행 수 상한 */
  GROUP_CAP: 7,
  BASE_MOB_CAP: 14,
  MIN_MOB_CAP: 5,
  /** 압축 최대치에서 개체 질 배율 (1 + 이 값) */
  COMPRESSION_QUALITY: 0.9,
  /** 압축 최대치에서 스폰 상한 계수 (0.35 = 원래의 35%) */
  CAP_FLOOR: 0.35,
} as const;

export const GRADES: readonly Grade[] = ['F', 'E', 'D', 'C', 'B', 'A', 'S'];

/** 등급별 표시색 (0xRRGGBB). */
export const GRADE_COLOR: readonly number[] = [
  0xb9bfa9, 0x94c07c, 0x6fbfa4, 0x6a9fd8, 0xb07ad8, 0xe8b34b, 0xf4efd8,
];

/**
 * 시즌 운영. 지금은 **끝없는 세계** — 마왕은 쓰러지지 않고 진행이 계속된다.
 * (시즌제 도입 여부는 추후 결정)
 */
export const SEASON = {
  ENDLESS: true,
  /** 끝없는 세계에서 마왕이 빈사 상태가 되면 이 비율로 회복하고 버틴다 */
  KING_REVIVE_RATIO: 0.35,
} as const;

/**
 * 승급 임계 = 누적 킬 (grade+1)*KILLS_MULT.
 * 값이 클수록 상위 등급이 귀해진다.
 */
export const GRADE_UP = {
  KILLS_MULT: 8,
} as const;

/** 이름 생성용 음절. */
export const SYLLABLES = {
  A: ['강', '김', '박', '서', '한', '무', '철', '달', '은', '독'],
  B: ['도끼', '모험', '방패', '화살', '그림자', '돌쇠', '새벽', '칼날', '여울', '묘연'],
} as const;

/** 경제 초기값. */
export const ECONOMY = {
  START_GOLD: 500,
  PARCEL_COST: 60,
  FOREST_CLEAR_COST: 30,
  REPAIR_COST: 40,
} as const;

/** 시설 정의. cost=건설비, kind=기능, stockMax/rate=생산, price=판매가. (프로토타입 값) */
export type BuildDef = {
  name: string;
  icon: string;
  cost: number;
  kind: BuildKind;
  stockMax: number;
  rate: number;
  price: number;
};

export const PARCEL_BUILDS: Record<ParcelType, Record<BuildId, BuildDef>> = {
  village: {
    smith: { name: '대장간', icon: '⚒', cost: 110, kind: 'shop', stockMax: 4, rate: 9, price: 45 },
    farm: { name: '밭', icon: '🌾', cost: 50, kind: 'eat', stockMax: 8, rate: 6, price: 8 },
    inn: { name: '여관', icon: '🛏', cost: 90, kind: 'inn', stockMax: 5, rate: 8, price: 16 },
    tower: { name: '망루', icon: '🗼', cost: 120, kind: 'def', stockMax: 0, rate: 0, price: 0 },
    deco: { name: '정원', icon: '🌸', cost: 25, kind: 'deco', stockMax: 0, rate: 0, price: 0 },
  } as Record<BuildId, BuildDef>,
  dungeon: {
    den: { name: '마물 둥지', icon: '🕸', cost: 95, kind: 'den', stockMax: 0, rate: 0, price: 0 },
    chest: { name: '보물상자', icon: '📦', cost: 75, kind: 'chest', stockMax: 0, rate: 0, price: 0 },
    torch: { name: '횃불', icon: '🔥', cost: 22, kind: 'torch', stockMax: 0, rate: 0, price: 0 },
  } as Record<BuildId, BuildDef>,
};

/** 사천왕 (고정 배치, 북쪽일수록 강함). [col,row,hp,pow] + 이름. */
export const BOSSES: readonly { col: number; row: number; hp: number; pow: number; name: string }[] = [
  { col: 2, row: 3, hp: 2200, pow: 62, name: '잿가루' },
  { col: 7, row: 2, hp: 3400, pow: 88, name: '밤울음' },
  { col: 1, row: 1, hp: 4800, pow: 118, name: '서리이빨' },
  { col: 8, row: 1, hp: 6500, pow: 150, name: '핏빛손톱' },
];

export const DEMON_KING = { hp: 60000, pow: 320, name: '마왕' } as const;

// ─────────────────────────────────────────────────────────────
// M2 — 자율 개체 고도화 밸런스 노브 (여기만 고치면 밸런스가 바뀐다)
// ─────────────────────────────────────────────────────────────

/** 욕구 → 행동 후보 점수화 계수. 판단 파이프라인의 핵심 노브. */
export const DESIRE = {
  /** 회복(여관): hp 비율이 이 값 밑으로 내려가면 욕구 발생 */
  REST_THRESHOLD: 0.55,
  REST_BASE: 1.15,
  /** 식사(밭): 허기가 이 값을 넘으면 욕구 발생 */
  EAT_THRESHOLD: 55,
  EAT_MAX: 110,
  EAT_BASE: 1.0,
  /** 장비 갈망(대장간) */
  GEAR_BASE: 0.9,
  /** 성장(던전 훈련) */
  TRAIN_BASE: 0.65,
  /** 자금 욕구: 이 밑이면 부족으로 보고 저위험 사냥 선호 */
  FUNDS_LOW: 60,
  FUNDS_BASE: 0.55,
  /** 일반 사냥 기본 점수 */
  HUNT_BASE: 0.6,
  /** 보스(사천왕/마왕) 도전 기본 점수 */
  BOSS_BASE: 0.45,
  /** 전투력이 보스 위협 대비 충분할 때의 가산 (준비되면 도전한다) */
  BOSS_READY_BONUS: 0.6,
  /** 준비도 기준: power / (boss.pow * 이 값) 이 1이면 완전 준비 */
  BOSS_READY_SCALE: 5,
  /** 거리 페널티 나눗수 — 보스는 멀어도 찾아간다(값이 클수록 거리 영향 작음) */
  BOSS_DIST_DIV: 1500,
  HUNT_DIST_DIV: 600,
  /** 배회 (아무것도 안 끌릴 때의 바닥 점수) */
  WANDER: 0.05,
  /** 위험도 페널티 계수: 적 위협/아군 전투력 비율에 곱해짐 (신중할수록 크게 작용) */
  RISK_PENALTY: 1.35,
} as const;

/** 무기 티어 — 등급별 수요 차등(고등급일수록 상위 티어를 원하고, 값도 비싸다). */
export const WEAPON = {
  MAX_TIER: 3,
  /** 티어 t 무기 가격 = PRICE_PER_TIER * t */
  PRICE_PER_TIER: 45,
} as const;

/** 등급 g가 원하는 최소 무기 티어. 저등급은 싸구려에서 멈추고, 고등급만 상위 티어를 산다. */
export function wantWeaponTier(grade: number): number {
  return Math.min(WEAPON.MAX_TIER, Math.floor(grade / 2) + 1);
}

/**
 * 파티 — 결성/리더 교대/해산.
 * 확률은 전부 **초당(per second)** 기준이며 dt를 곱해 적용한다(프레임레이트 독립).
 */
export const PARTY = {
  MERGE_DIST: 5,
  /** 솔로끼리 조우 시 초당 결성 확률 (30fps에서 프로토타입의 프레임당 0.012와 등가) */
  MERGE_CHANCE: 0.35,
  /** 다른 멤버 등급이 리더보다 이만큼 높으면 리더 교대 검토 */
  LEADER_SWAP_MARGIN: 2,
  /** 초당 리더 교대 확률 */
  LEADER_SWAP_CHANCE: 0.9,
  /** 초당 기본 해산 확률 (의리가 낮고 인원이 많을수록 커짐) */
  DISBAND_BASE: 0.0012,
} as const;
